import {Notification} from '../../modules/notification.js';
import {Spinner} from '../../modules/spinner.js';
import '../../modules/button.js';
import {getHttpClient} from '../../modules/httpClient.js';
import {dangerouslyConvertHtmlStringToElement} from '../../utils/markupHelpers.js';

let httpClient = getHttpClient();

/**
 * Use custom settings for Wysiwyg editor
 */
function initRTE() {
    document.querySelectorAll('rt-editor').forEach((editor) => {
        const submitButtons = editor.closest('form').querySelectorAll('button[type="submit"]');
        if (submitButtons.length) {
            editor.addEventListener('rte:change', () => {
                const isTextareaEmpty = editor.value === '';
                submitButtons[0].disabled = isTextareaEmpty;
            });
            editor.addEventListener('rte:focus', () => {
                const messageBlock = editor.closest('.js-add-message-block');
                if (messageBlock) {
                    messageBlock.classList.add('addMessageBlock--focused');
                }
            });
        }

        /* Only do This on lesson pages */
        /* Push "next" and "prev" pages links inside `.discussionContainer` */
        /* We are doing this so that we can make the RTE, next/prev links sticky */
        const lessonPager = document.querySelector('.lessonPager');
        const discussionContainer = document.querySelector('.lessonItemSpecial .discussionContainer');

        // Only add 'inside-discussion' class if both discussionContainer and lessonPager are present
        if (lessonPager && discussionContainer) {
            lessonPager.classList.add('inside-discussion');
            discussionContainer.append(lessonPager);
        }
    });
}

/**
 * Return root message for replies
 * @param {HTMLElement} child
 * @returns {?HTMLElement}
 */
function getRootMessageByChild(child) {
    return child.closest('.js-target-message-root');
}

/**
 * Get message up to one level
 * @param {HTMLElement} child
 * @returns {?HTMLElement}
 */
function getMessageByChild(child) {
    return child.closest('.js-target-message');
}

/**
 * @param {HTMLElement} message
 */
function removeEditFormElements(message) {
    message.querySelector('rt-editor').remove();
    message.querySelector('.js-target-edit-message-controls').remove();
}

/** [no description; please add] */
function listenNewMessageSubmit() {
    /** @param {HTMLFormElement} form */
    async function ajaxNewMessageSubmission(form) {
        const formData = new FormData(form);

        /** @type {null|{html: string, model: { like_count: number, message_text_raw: string, member_id: number, discussion_id: number, message_text: string, updated_at: string, created_at: string, id: number, reply_to_id?: number}}} */
        const response = await httpClient.post(form.action, formData).catch((error) => {
            let errorMessage = error.message;

            if (error.status === 422 && error.errorBody?.errors?.message_text) {
                errorMessage = error.errorBody.errors.message_text.join('\n');
            }

            Notification.error(errorMessage, form);
        });

        if (response) {
            const newMessage = dangerouslyConvertHtmlStringToElement(response.html);
            const isReply = response.model && response.model.reply_to_id;
            const editor = form.querySelector('rt-editor');
            if (isReply) {
                const addMessageBlock = form.closest('.js-reply-template');
                addMessageBlock.hidden = true;
                const parentRootMessage = getRootMessageByChild(form);
                parentRootMessage.querySelector('.js-target-replies').append(newMessage);
            } else {
                const newMessageContainer = document.querySelector('.js-new-message-container');
                const orderBy = newMessageContainer.dataset.orderBy;
                if (['popular', 'asc'].includes(orderBy)) {
                    newMessageContainer.append(newMessage);
                } else {
                    newMessageContainer.prepend(newMessage);
                }
            }

            // "highlight" the new message (also using :target CSS pseudo-class)
            history.pushState(null, '', `#m${response.model.id}`);

            editor.value = '';
            scrollViewportToElement(newMessage);

            // If the user attempted to submit an invalid message before,
            // and there's still a "leftover" error message on the form…
            // but they has now submitted a valid message, we need to remove
            // the old error message since it doesn't make sense anymore.
            form.querySelector('.alertContainer')?.remove();

            document.querySelectorAll('.js-no-messages-container')
              .forEach((stubMessage) => stubMessage.classList.add('hidden'));
        }

        form.querySelector('.ixdf-spinner').classList.remove('button--loading');
    }

    document.addEventListener('submit', (event) => {
        const postDiscussionMessageForm = event.target.closest('.js-new-message-form');
        if (postDiscussionMessageForm instanceof HTMLFormElement) {
            event.preventDefault();
            ajaxNewMessageSubmission(postDiscussionMessageForm);
        }
    });
}

/**
 * @param {HTMLElement} message
 * @param {number} likeDifference
 */
function toggleLikedStatus(message, likeDifference) {
    const likeCount = message.querySelector('.js-target-like-count');
    const likeButtonSvg = message.querySelector('.js-like-btn > svg > use');

    const currentNumberOfLikes = parseInt(likeCount.textContent) + likeDifference;
    const currentNumberOfLikesLabel = currentNumberOfLikes === 1 ? 'like' : 'likes';
    likeCount.textContent = `${currentNumberOfLikes} ${currentNumberOfLikesLabel}`;

    const spriteUrl = likeButtonSvg.getAttribute('xlink:href').split('#')[0];
    const svgUrl = spriteUrl + '#' + (likeDifference > 0 ? 'heart-filled' : 'heart');
    likeButtonSvg.setAttribute('xlink:href', svgUrl);

    message.classList.toggle('js-liked');
}

/** @param {HTMLElement} message */
function addLikedStatus(message) {
    toggleLikedStatus(message, 1);
}

/**
 * Change DOM elements/layout only
 * @param {HTMLElement} message
 */
function removeLikedStatus(message) {
    toggleLikedStatus(message, -1);
}

/**
 * Send like request to the server using ajax, update UI elements
 * @param {HTMLElement} message
 */
async function likeMessage(message) {
    const likeButton = message.querySelector('.js-like-btn');
    likeButton.disabled = true;
    likeButton.classList.remove('animate-liked');

    addLikedStatus(message);

    const messageId = message.dataset.messageId;

    // @route 'discussion.message.like.store'
    await httpClient.post(`/conversation/like/message/${messageId}`).catch((error) => {
        const isLikeAlreadyExist = error.status === 409;
        if (isLikeAlreadyExist) {
            // message already liked, decrease like count
            removeLikedStatus(message);
        }

        Notification.error(error.message);
    });

    likeButton.disabled = false;
    likeButton.classList.add('animate-liked');
}

/**
 * Send unlike request to the server using ajax, update UI elements
 * @param {HTMLElement} message
 */
async function unlikeMessage(message) {
    const likeButton = message.querySelector('.js-like-btn');
    likeButton.disabled = true;
    likeButton.classList.remove('animate-liked');

    removeLikedStatus(message);

    const messageId = message.dataset.messageId;

    // @route 'discussion.message.like.delete'
    await httpClient.delete(`/conversation/like/message/${messageId}`).catch((error) => {
        const isLikeAlreadyExist = error.status === 409;
        if (isLikeAlreadyExist) {
            // message already disliked, return it back
            addLikedStatus(message);
        }

        Notification.error(error.message);
    });

    likeButton.disabled = false;
    likeButton.classList.add('animate-liked');
}

/**
 * Add Listener for like/unlike clicks
 */
function listenLikeClicks() {
    document.addEventListener('click', (event) => {
        const likeButton = event.target.closest('.js-like-btn');
        if (likeButton) {
            event.preventDefault();
            const message = likeButton.closest('.js-target-message');

            const isMessageAlreadyLiked = message.classList.contains('js-liked');
            if (!isMessageAlreadyLiked) {
                likeMessage(message);
            } else {
                unlikeMessage(message);
            }
        }
    });
}

/**
 * Add event listener for 'Reply' button
 */
function listenReplyClick() {
    document.addEventListener('click', (event) => {
        const replyButton = event.target.closest('.js-reply');
        if (!replyButton) {
            return;
        }

        event.preventDefault();
        const message = getMessageByChild(event.target);
        const replyContainer = message.querySelector('.js-target-replies');

        // Animate out the current open reply
        document.querySelectorAll('.animated').forEach((replyBlock) => {
            replyBlock.classList.remove('animated');
        });

        const replyTemplate = document.querySelector('.js-reply-template');
        replyTemplate.querySelector('.js-reply_to_message_id-input').value = message.dataset.messageId;
        replyContainer.append(replyTemplate);
        replyTemplate.hidden = false;
        const editor = replyTemplate.querySelector('rt-editor');
        setTimeout(() => {
            editor.focus();
            scrollViewportToElement(editor);
        }, 0);

        replyContainer.classList.remove('animated');
        replyContainer.classList.add('animating');
        setTimeout(() => {
            replyContainer.classList.remove('animating');
            replyContainer.classList.add('animated');
        }, 400);
    });
}

/**
 * @param {HTMLElement} element
 */
function scrollViewportToElement(element) {
    // Check if the element is fully visible in viewport
    const rect = element.getBoundingClientRect();
    const isInViewport = (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth
    );

    // Only scroll if the element is not fully visible
    if (!isInViewport) {
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    }
}

/**
 * Add event listener for 'Edit Message' button click
 */
function listenEditMessageClick() {
    document.addEventListener('click', (event) => {
        const editButton = event.target.closest('.js-editDiscussionMessage');
        if (editButton instanceof HTMLElement) {
            event.preventDefault();

            const messageBodyDetails = event.target.closest('[data-message-body-details]');
            messageBodyDetails.hidden = true;
            const message = getMessageByChild(event.target);
            const messageBody = message.querySelector('.js-target-text');
            const editor = document.createElement('rt-editor');
            const uploadDestination = editButton.dataset.uploadDestination;
            if (uploadDestination) {
                editor.setAttribute('accept', editButton.dataset.accept);
                editor.setAttribute('upload-destination', uploadDestination);
            }
            editor.addEventListener('rte:connected', () => {
                messageBody.hidden = true;

                const editMessageButtonsContainer = document.querySelector('.js-edit-message-template');
                editor.insertAdjacentHTML('afterend', editMessageButtonsContainer.innerHTML);

                editor.value = messageBody.innerHTML;
                editor.focus();
            });

            messageBody.parentNode.insertBefore(editor, messageBody);
        }
    });
}

/**
 * Add event listener for 'Save Changes' button click
 */
function listenSaveChangesClick() {
    document.addEventListener('click', async (event) => {
        const saveButton = event.target.closest('.js-save-message');
        if (saveButton) {
            const message = getMessageByChild(event.target);
            const messageId = message.dataset.messageId;
            const spinner = new Spinner(message);
            const messageBodyDetailsContainer = event.target.closest('[data-discussion-message-body]');
            spinner.start();

            const data = {
                message_text: message.querySelector('rt-editor').value,
            };

            // @route 'discussion.message.edit'
            const response = await httpClient.put(`/conversation/message/${messageId}`, data).catch((error) => {
                let errorMessage = error.message;

                if (error.status === 422 && error.errorBody?.errors?.message_text) {
                    errorMessage = error.errorBody.errors.message_text.join('\n');
                }

                Notification.error(errorMessage, message.querySelector('.js-target-edit-message-controls'));
            });

            if (response) {
                const editableElement = message.querySelector('.js-target-text');
                editableElement.hidden = false;
                editableElement.innerHTML = response.message_text;

                removeEditFormElements(message);
            }

            spinner.stop();
            window.scrollY -= 1; // hack to re-locate RTE
            const messageBodyDetails = messageBodyDetailsContainer.querySelector('[data-message-body-details]');
            messageBodyDetails.hidden = false;
        }
    });
}

/**
 * Add event listener for 'Cancel Changes' button click
 */
function listenCancelChangesClick() {
    document.addEventListener('click', (event) => {
        const cancelButton = event.target.closest('.js-close-message-editor');
        if (cancelButton) {
            const message = getMessageByChild(event.target);
            const editableElement = message.querySelector('.js-target-text');
            editableElement.hidden = false;

            const messageBodyDetailsContainer = event.target.closest('[data-discussion-message-body]');
            const messageBodyDetails = messageBodyDetailsContainer.querySelector('[data-message-body-details]');
            messageBodyDetails.hidden = false;

            removeEditFormElements(message);
        }
    });
}

/**
 * Add event listener for 'delete message' button
 */
function listenDeleteMessageClick() {
    document.querySelectorAll('#deleteMessageDialog,#deleteReplyDialog').forEach((dialogElement) => {
        let originalMessage;

        dialogElement.addEventListener('open', (event) => {
            const activationElement = event.detail;
            if (!activationElement) {
                event.preventDefault();
            } else {
                originalMessage = getMessageByChild(activationElement);
            }
        });
        dialogElement.addEventListener('close', async () => {
            if (dialogElement.returnValue === 'DELETE_ACTION') {
                const messageId = originalMessage.dataset.messageId;
                const spinner = new Spinner(originalMessage);
                spinner.start();
                try {
                    // @route 'discussion.message.delete'
                    const response = httpClient.delete(`/conversation/message/${messageId}`);
                    if (response) {
                        originalMessage.remove();
                    }
                } catch (error) {
                    Notification.error(error.message, originalMessage);
                } finally {
                    spinner.stop();
                }
            }
        });
    });
}

/** [no description; please add] */
function listenChangeOrder() {
    const selectInput = document.querySelector('[data-discussion-topic-order]');
    if (selectInput) {
        selectInput.addEventListener('change', ({target}) => {
            let parameters = new URLSearchParams(document.location.search.substring(1));
            parameters.set('orderBy', target.value);
            window.location.search = parameters.toString();
        });
    }
}

/**
 * Init page, register event handlers
 */
function initPage() {
    initRTE();
    listenNewMessageSubmit();
    listenLikeClicks();
    listenReplyClick();
    listenEditMessageClick();
    listenSaveChangesClick();
    listenCancelChangesClick();
    listenDeleteMessageClick();
    listenChangeOrder();
}

// init page when the document is ready
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initPage) : initPage();
